import React, { useEffect, useState } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import { X, Download, ZoomIn, ZoomOut, RotateCw } from 'lucide-react'

const FileViewer = ({ data, onClose }) => {
  const [objectUrl, setObjectUrl] = useState(null)
  const [zoom, setZoom] = useState(1)
  const [rotation, setRotation] = useState(0)

  useEffect(() => {
    if (data?.data) {
      const blob = new Blob([data.data], { type: data.mimeType })
      const url = URL.createObjectURL(blob)
      setObjectUrl(url)

      return () => {
        URL.revokeObjectURL(url)
      }
    }
  }, [data])

  const handleDownload = () => {
    if (objectUrl) {
      const a = document.createElement('a')
      a.href = objectUrl
      a.download = data.fileName
      document.body.appendChild(a)
      a.click()
      document.body.removeChild(a)
    }
  }

  const handleZoomIn = () => setZoom(prev => Math.min(prev + 0.25, 3))
  const handleZoomOut = () => setZoom(prev => Math.max(prev - 0.25, 0.25))
  const handleRotate = () => setRotation(prev => (prev + 90) % 360)

  const renderContent = () => {
    if (!objectUrl) return null

    const { mimeType, fileName } = data

    if (mimeType.startsWith('image/')) {
      return (
        <div className="flex items-center justify-center w-full h-full overflow-auto">
          <motion.img
            src={objectUrl}
            alt={fileName}
            className="max-w-none"
            style={{
              transform: `scale(${zoom}) rotate(${rotation}deg)`,
              transition: 'transform 0.3s ease'
            }}
            drag
            dragConstraints={{ left: -500, right: 500, top: -500, bottom: 500 }}
          />
        </div>
      )
    }

    if (mimeType.startsWith('video/')) {
      return (
        <div className="flex items-center justify-center w-full h-full">
          <video
            src={objectUrl}
            controls
            className="max-w-full max-h-full"
            style={{
              transform: `scale(${zoom})`,
              transition: 'transform 0.3s ease'
            }}
          />
        </div>
      )
    }

    if (mimeType === 'application/pdf' || mimeType === 'text/plain') {
      return (
        <iframe
          src={objectUrl}
          className="w-full h-full bg-white"
          title={fileName}
        />
      )
    }

    // Fallback for unsupported file types
    return (
      <div className="flex items-center justify-center w-full h-full">
        <div className="text-center text-white">
          <div className="w-24 h-24 mx-auto mb-4 rounded-full bg-white/20 flex items-center justify-center">
            <X className="w-12 h-12" />
          </div>
          <h3 className="text-xl font-semibold mb-2">Cannot Preview</h3>
          <p className="text-gray-300 mb-6">
            This file type cannot be previewed in the browser.
          </p>
          <button
            onClick={handleDownload}
            className="btn-primary"
          >
            <Download className="w-4 h-4 mr-2" />
            Download File
          </button>
        </div>
      </div>
    )
  }

  const isImage = data?.mimeType?.startsWith('image/')
  const isVideo = data?.mimeType?.startsWith('video/')

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 bg-black/90 backdrop-blur-sm flex flex-col z-50"
      >
        {/* Header - Mobile Responsive */}
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-black/50"
        >
          {/* Top row - File name and close button */}
          <div className="flex items-center justify-between p-3 sm:p-4">
            <h2 className="text-white text-sm sm:text-lg font-semibold truncate flex-1 mr-4">
              {data?.fileName}
            </h2>
            
            <button
              onClick={onClose}
              className="text-white hover:text-gray-300 p-2 rounded-lg hover:bg-white/10 transition-colors flex-shrink-0"
              title="Close"
            >
              <X className="w-5 h-5 sm:w-6 sm:h-6" />
            </button>
          </div>
          
          {/* Bottom row - Controls (mobile: separate row, desktop: inline) */}
          {(isImage || isVideo) && (
            <div className="flex items-center justify-between px-3 pb-3 sm:px-4 sm:pb-4 sm:pt-0">
              <div className="flex items-center gap-1 sm:gap-2">
                <button
                  onClick={handleZoomOut}
                  className="text-white hover:text-gray-300 p-1.5 sm:p-2 rounded-lg hover:bg-white/10 transition-colors"
                  title="Zoom Out"
                >
                  <ZoomOut className="w-4 h-4 sm:w-5 sm:h-5" />
                </button>
                
                <span className="text-white text-xs sm:text-sm px-1 sm:px-2 min-w-[40px] text-center">
                  {Math.round(zoom * 100)}%
                </span>
                
                <button
                  onClick={handleZoomIn}
                  className="text-white hover:text-gray-300 p-1.5 sm:p-2 rounded-lg hover:bg-white/10 transition-colors"
                  title="Zoom In"
                >
                  <ZoomIn className="w-4 h-4 sm:w-5 sm:h-5" />
                </button>
                
                {isImage && (
                  <button
                    onClick={handleRotate}
                    className="text-white hover:text-gray-300 p-1.5 sm:p-2 rounded-lg hover:bg-white/10 transition-colors"
                    title="Rotate"
                  >
                    <RotateCw className="w-4 h-4 sm:w-5 sm:h-5" />
                  </button>
                )}
              </div>

              <button
                onClick={handleDownload}
                className="text-white hover:text-gray-300 p-1.5 sm:p-2 rounded-lg hover:bg-white/10 transition-colors"
                title="Download"
              >
                <Download className="w-4 h-4 sm:w-5 sm:h-5" />
              </button>
            </div>
          )}
          
          {/* Download button for non-image/video files */}
          {!(isImage || isVideo) && (
            <div className="flex justify-end px-3 pb-3 sm:px-4 sm:pb-4">
              <button
                onClick={handleDownload}
                className="text-white hover:text-gray-300 p-1.5 sm:p-2 rounded-lg hover:bg-white/10 transition-colors"
                title="Download"
              >
                <Download className="w-4 h-4 sm:w-5 sm:h-5" />
              </button>
            </div>
          )}
        </motion.div>

        {/* Content */}
        <motion.div
          initial={{ opacity: 0, scale: 0.9 }}
          animate={{ opacity: 1, scale: 1 }}
          exit={{ opacity: 0, scale: 0.9 }}
          className="flex-1 flex items-center justify-center p-2 sm:p-4 min-h-0 overflow-hidden"
        >
          {renderContent()}
        </motion.div>

        {/* Footer with file info - Mobile Responsive */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="p-3 sm:p-4 bg-black/50 text-center"
        >
          <p className="text-gray-300 text-xs sm:text-sm break-words">
            🔒 This file was decrypted locally in your browser
            <span className="hidden sm:inline"> • Type: {data?.mimeType}</span>
            <span className="hidden sm:inline"> • Size: {data?.data ? formatFileSize(data.data.byteLength) : 'Unknown'}</span>
          </p>
          {/* Mobile: Show type and size on separate lines */}
          <div className="sm:hidden mt-1 space-y-1">
            <p className="text-gray-400 text-xs">Type: {data?.mimeType}</p>
            <p className="text-gray-400 text-xs">Size: {data?.data ? formatFileSize(data.data.byteLength) : 'Unknown'}</p>
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  )
}

// Helper function to format file size
const formatFileSize = (bytes) => {
  if (!bytes) return '0 B'
  
  const sizes = ['B', 'KB', 'MB', 'GB']
  const i = Math.floor(Math.log(bytes) / Math.log(1024))
  
  return `${(bytes / Math.pow(1024, i)).toFixed(1)} ${sizes[i]}`
}

export default FileViewer
